import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import StorefrontLayout from "../shared/StorefrontLayout";
import { t, getLang } from "../../i18n";
import { NEWS_ITEMS } from "./blogContent";

const BLOG_CATEGORIES = [
  { key: "all", values: ["all"] },
  { key: "buyingGuide", values: ["buying guide", "buying", "rfq", "quotation"] },
  { key: "customization", values: ["customization", "custom", "oem", "odm", "private label"] },
  { key: "sampleOrder", values: ["sample order", "sample"] },
  { key: "sourcing", values: ["sourcing", "supply", "compliance", "moq"] },
  { key: "productTips", values: ["product tips", "tips", "material", "spec"] }
];

const BLOG_PAGE_SIZE = 6;

function normalizeCategory(value) {
  const raw = String(value || "").trim();
  if (!raw) {
    return "sourcing";
  }

  const lowered = raw.toLowerCase();
  for (const category of BLOG_CATEGORIES) {
    if (category.key === "all") {
      continue;
    }

    if (category.values.some((item) => lowered.includes(item))) {
      return category.key;
    }
  }

  return "sourcing";
}

function normalizeDate(value) {
  const raw = String(value || "").trim();
  if (!raw) {
    return "";
  }

  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toISOString();
  }

  return "";
}

function formatPublishDate(value) {
  if (!value) {
    return t("blogDateFallback");
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return t("blogDateFallback");
  }

  return new Intl.DateTimeFormat("en", {
    year: "numeric",
    month: "short",
    day: "numeric"
  }).format(parsed);
}

function getTextExcerpt(text, limit = 150) {
  const raw = String(text || "").trim();
  if (!raw) {
    return "";
  }

  if (raw.length <= limit) {
    return raw;
  }

  return `${raw.slice(0, limit).trim()}...`;
}

function normalizeTags(input) {
  if (Array.isArray(input)) {
    return input
      .map((item) => String(item || "").trim())
      .filter(Boolean)
      .slice(0, 8);
  }

  const raw = String(input || "").trim();
  if (!raw) {
    return [];
  }

  return raw
    .split(/[;,|]/)
    .map((item) => String(item || "").trim())
    .filter(Boolean)
    .slice(0, 8);
}

function resolveReadMoreUrl(item) {
  const candidates = [item?.readMoreUrl, item?.url, item?.link, item?.path];
  const matched = candidates.find((candidate) => String(candidate || "").trim());
  return String(matched || "").trim();
}

function normalizeBlogItem(item, index = 0) {
  const currentLang = getLang();
  const title = (currentLang === "zh" ? item?.title_zh : item?.title) || String(item?.title || item?.heading || item?.name || "").trim();
  const body = (currentLang === "zh" ? item?.body_zh : item?.body) || String(item?.body || item?.content || "").trim();
  const summary = (currentLang === "zh" ? item?.summary_zh : item?.summary) || String(item?.summary || item?.description || item?.excerpt || "").trim();
  const excerpt = summary || getTextExcerpt(body, 170);
  const categoryKey = normalizeCategory(item?.category || item?.topic || item?.type || item?.articleType);
  const categoryRaw = String(item?.category || item?.topic || item?.type || item?.articleType || "").trim();
  const cover = String(item?.cover || item?.coverImage || item?.coverUrl || item?.image || item?.imageUrl || item?.thumbnail || "").trim();
  const dateIso = normalizeDate(item?.publishedAt || item?.publishDate || item?.date || item?.createdAt || item?.updatedAt);
  const tags = normalizeTags(item?.tags || item?.keywords || item?.topics);
  const readMoreUrl = resolveReadMoreUrl(item);
  const stableId = String(item?.id || item?.slug || title || `article-${index + 1}`).trim();
  
  // Calculate reading time based on content length
  const words = (body || excerpt).trim().split(/\s+/).length;
  const readingTime = Math.max(1, Math.ceil(words / 200));

  if (!title) {
    return null;
  }

  return {
    id: stableId,
    title,
    body: body || excerpt,
    excerpt: excerpt || getTextExcerpt(body, 170),
    categoryKey,
    categoryRaw,
    cover,
    dateIso,
    dateLabel: formatPublishDate(dateIso),
    tags,
    readMoreUrl,
    featured: item?.featured === true,
    readingTime
  };
}

function withFallbackBody(items) {
  return items.map((item) => ({
    ...item,
    body: item.body || item.excerpt || ""
  }));
}

export default function NewsPage({ apiBaseUrl }) {
  const [currency, setCurrency] = useState("USD");
  const [blogItems, setBlogItems] = useState(() => withFallbackBody(NEWS_ITEMS.map((item, index) => normalizeBlogItem(item, index)).filter(Boolean)));
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedArticles, setExpandedArticles] = useState({});

  const normalizedSearchKeyword = searchKeyword.trim().toLowerCase();

  const blogMetaTitle = t("blogSeoTitle");
  const blogMetaDescription = t("blogSeoDescription");

  const categoryLabelMap = useMemo(
    () => ({
      all: t("blogCategoryAll"),
      buyingGuide: t("blogCategoryBuyingGuide"),
      customization: t("blogCategoryCustomization"),
      sampleOrder: t("blogCategorySampleOrder"),
      sourcing: t("blogCategorySourcing"),
      productTips: t("blogCategoryProductTips")
    }),
    []
  );

  const getCategoryLabel = (categoryKey, fallbackValue = "") => {
    if (categoryKey && categoryLabelMap[categoryKey]) {
      return categoryLabelMap[categoryKey];
    }

    return String(fallbackValue || categoryLabelMap.sourcing || "").trim();
  };

  const canonicalUrl = useMemo(() => {
    if (typeof window === "undefined") {
      return "";
    }

    return `${window.location.origin}/blog`;
  }, []);

  const filteredItems = useMemo(
    () =>
      blogItems.filter((item) => {
        const categoryMatched = selectedCategory === "all" || item.categoryKey === selectedCategory;
        if (!categoryMatched) {
          return false;
        }

        if (!normalizedSearchKeyword) {
          return true;
        }

        const searchableText = [item.title, item.excerpt, item.body, item.categoryRaw, ...(item.tags || [])]
          .join(" ")
          .toLowerCase();

        return searchableText.includes(normalizedSearchKeyword);
      }),
    [blogItems, normalizedSearchKeyword, selectedCategory]
  );

  const featuredArticle = useMemo(() => {
    if (!filteredItems.length) {
      return null;
    }

    return filteredItems.find((item) => item.featured) || filteredItems[0];
  }, [filteredItems]);

  const latestArticles = useMemo(() => {
    if (!filteredItems.length) {
      return [];
    }

    return filteredItems.filter((item) => item.id !== featuredArticle?.id);
  }, [filteredItems, featuredArticle]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(latestArticles.length / BLOG_PAGE_SIZE)), [latestArticles.length]);

  const paginatedArticles = useMemo(() => {
    const start = (currentPage - 1) * BLOG_PAGE_SIZE;
    return latestArticles.slice(start, start + BLOG_PAGE_SIZE);
  }, [currentPage, latestArticles]);

  const topicList = useMemo(() => {
    const seeded = BLOG_CATEGORIES.filter((item) => item.key !== "all").map((item) => item.key);
    const dynamic = new Set();

    blogItems.forEach((item) => {
      if (item.categoryKey && item.categoryKey !== "all") {
        dynamic.add(item.categoryKey);
      }
      (item.tags || []).forEach((tag) => {
        const normalized = String(tag || "").trim();
        if (normalized) {
          dynamic.add(normalized);
        }
      });
    });

    return Array.from(new Set([...seeded, ...Array.from(dynamic)])).slice(0, 20);
  }, [blogItems]);

  const blogStructuredData = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "Blog",
      name: "Insights & Buying Guides",
      url: canonicalUrl || undefined,
      inLanguage: "en",
      description: blogMetaDescription,
      blogPost: filteredItems.slice(0, 12).map((item) => ({
        "@type": "BlogPosting",
        headline: item.title,
        description: item.excerpt || item.body,
        articleSection: getCategoryLabel(item.categoryKey, item.categoryRaw),
        datePublished: item.dateIso || undefined,
        image: item.cover || undefined,
        url: item.readMoreUrl || canonicalUrl || undefined
      }))
    }),
    [filteredItems, canonicalUrl, blogMetaDescription]
  );

  useEffect(() => {
    let cancelled = false;

    async function loadBlogItems() {
      if (!apiBaseUrl) {
        return;
      }

      try {
        const response = await fetch(`${apiBaseUrl}/storefront/config`, { cache: "no-store" });
        if (!response.ok) {
          throw new Error("Failed to load storefront config");
        }

        const data = await response.json();
        if (cancelled) {
          return;
        }

        const normalized = Array.isArray(data?.storeSettings?.blogArticles)
          ? data.storeSettings.blogArticles
              .map((item, index) => normalizeBlogItem(item, index))
              .filter(Boolean)
              .slice(0, 20)
          : [];

        if (normalized.length) {
          setBlogItems(withFallbackBody(normalized));
        }
      } catch (_err) {
        if (!cancelled) {
          setBlogItems(withFallbackBody(NEWS_ITEMS.map((item, index) => normalizeBlogItem(item, index)).filter(Boolean)));
        }
      }
    }

    loadBlogItems();

    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl]);

  useEffect(() => {
    setCurrentPage(1);
  }, [normalizedSearchKeyword, selectedCategory]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  function toggleExpandedArticle(articleId) {
    setExpandedArticles((prev) => ({
      ...prev,
      [articleId]: !prev[articleId]
    }));
  }

  function renderReadMoreAction(item, variant = "inline") {
    if (!item) {
      return null;
    }

    if (item.readMoreUrl) {
      const external = /^https?:\/\//i.test(item.readMoreUrl);
      return (
        <a
          className={`blog-hub-readmore ${variant === "button" ? "button" : ""}`.trim()}
          href={item.readMoreUrl}
          target={external ? "_blank" : undefined}
          rel={external ? "noreferrer" : undefined}
        >
          {t("blogReadMore")}
        </a>
      );
    }

    return (
      <button
        type="button"
        className={`blog-hub-readmore-toggle ${variant === "button" ? "button" : ""}`.trim()}
        onClick={() => toggleExpandedArticle(item.id)}
      >
        {expandedArticles[item.id] ? t("blogShowLess") : t("blogReadMore")}
      </button>
    );
  }

  function renderArticleText(item, limit = 170) {
    if (!item) {
      return "";
    }

    if (expandedArticles[item.id]) {
      return item.body || item.excerpt;
    }

    return getTextExcerpt(item.excerpt || item.body, limit);
  }

  useEffect(() => {
    const ensureMetaTag = (selector, attr, key, content) => {
      if (!content) {
        return;
      }

      let element = document.querySelector(selector);
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attr, key);
        document.head.appendChild(element);
      }
      element.setAttribute("content", content);
    };

    let descriptionMeta = document.querySelector('meta[name="description"]');
    if (!descriptionMeta) {
      descriptionMeta = document.createElement("meta");
      descriptionMeta.setAttribute("name", "description");
      document.head.appendChild(descriptionMeta);
    }
    descriptionMeta.setAttribute("content", blogMetaDescription);

    if (canonicalUrl) {
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement("link");
        canonical.setAttribute("rel", "canonical");
        document.head.appendChild(canonical);
      }
      canonical.setAttribute("href", canonicalUrl);
    }

    ensureMetaTag('meta[name="robots"]', "name", "robots", "index,follow");
    ensureMetaTag('meta[property="og:title"]', "property", "og:title", blogMetaTitle);
    ensureMetaTag('meta[property="og:description"]', "property", "og:description", blogMetaDescription);
    ensureMetaTag('meta[property="og:type"]', "property", "og:type", "website");
    ensureMetaTag('meta[property="og:url"]', "property", "og:url", canonicalUrl);
    ensureMetaTag('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    ensureMetaTag('meta[name="twitter:title"]', "name", "twitter:title", blogMetaTitle);
    ensureMetaTag('meta[name="twitter:description"]', "name", "twitter:description", blogMetaDescription);
  }, [canonicalUrl, blogMetaDescription, blogMetaTitle]);

  return (
    <StorefrontLayout apiBaseUrl={apiBaseUrl} currency={currency} onChangeCurrency={setCurrency} disableSeoAutofill={true}>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(blogStructuredData) }} />

      <section className="subpage-hero blog-hub-hero" aria-labelledby="blog-main-title">
        <p className="hero-kicker">{t("blogHeroKicker")}</p>
        <h1 id="blog-main-title">{t("blogHeroTitle")}</h1>
        <p>
          {t("blogHeroDescription")}
        </p>

        <div className="blog-hub-controls">
          <label className="blog-hub-search" htmlFor="blog-search-input">
            <span className="sr-only">{t("blogSearchAria")}</span>
            <input
              id="blog-search-input"
              type="search"
              placeholder={t("blogSearchPlaceholder")}
              value={searchKeyword}
              onChange={(event) => setSearchKeyword(event.target.value)}
            />
          </label>

          <nav className="blog-hub-categories" aria-label={t("blogCategoryAria")}>
            {BLOG_CATEGORIES.map((category) => (
              <button
                key={category.key}
                type="button"
                className={selectedCategory === category.key ? "is-active" : ""}
                onClick={() => setSelectedCategory(category.key)}
              >
                {getCategoryLabel(category.key)}
              </button>
            ))}
          </nav>
        </div>
      </section>

      <section className="blog-hub-featured" aria-labelledby="featured-article-title">
        <div className="section-title">
          <h2 id="featured-article-title">{t("blogFeaturedTitle")}</h2>
        </div>
        {featuredArticle ? (
          <article className="blog-hub-featured-card">
            <div className="blog-hub-featured-media" aria-hidden="true">
              {featuredArticle.cover ? (
                <img src={featuredArticle.cover} alt="" loading="lazy" />
              ) : (
                <div className="blog-hub-featured-placeholder">{t("blogFeaturedPlaceholder")}</div>
              )}
            </div>
            <div className="blog-hub-featured-body">
              <p className="blog-hub-meta">
                <span>
                  {getCategoryLabel(featuredArticle.categoryKey, featuredArticle.categoryRaw)}
                  <span className="blog-hub-meta-reading">
                    {featuredArticle.readingTime} {t("blogReadingTimeMin")}
                  </span>
                </span>
                <span>{featuredArticle.dateLabel}</span>
              </p>
              <h3>{featuredArticle.title}</h3>
              <p>{renderArticleText(featuredArticle, 240)}</p>
              {renderReadMoreAction(featuredArticle, "button")}
            </div>
          </article>
        ) : (
          <p className="blog-hub-empty">{t("blogEmptyFeatured")}</p>
        )}
      </section>

      <section className="blog-hub-latest" aria-labelledby="latest-articles-title">
        <div className="section-title">
          <h2 id="latest-articles-title">{t("blogLatestTitle")}</h2>
        </div>

        {paginatedArticles.length ? (
          <div className="blog-hub-grid">
            {paginatedArticles.map((item) => (
              <article key={item.id} className="blog-hub-card">
                <div className="blog-hub-card-media" aria-hidden="true">
                  {item.cover ? <img src={item.cover} alt="" loading="lazy" /> : <div className="blog-hub-card-placeholder">Insight</div>}
                </div>
                <div className="blog-hub-card-body">
                  <p className="blog-hub-meta">
                    <span>
                      {getCategoryLabel(item.categoryKey, item.categoryRaw)}
                      <span className="blog-hub-meta-reading">
                        {item.readingTime} {t("blogReadingTimeMin")}
                      </span>
                    </span>
                    <span>{item.dateLabel}</span>
                  </p>
                  <h3>{item.title}</h3>
                  <p>{renderArticleText(item, 155)}</p>
                  {renderReadMoreAction(item)}
                </div>
              </article>
            ))}
          </div>
        ) : (
          <p className="blog-hub-empty">{t("blogEmptyLatest")}</p>
        )}

        {latestArticles.length > BLOG_PAGE_SIZE ? (
          <nav className="market-pagination" aria-label={t("blogPaginationAria")}>
            <button
              type="button"
              onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
              disabled={currentPage === 1}
            >
              {t("productsPrev")}
            </button>
            <span>
              {t("productsPage")} {currentPage} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
              disabled={currentPage === totalPages}
            >
              {t("productsNext")}
            </button>
          </nav>
        ) : null}
      </section>

      <section className="subpage-panel blog-hub-topics" aria-labelledby="browse-topic-title">
        <h2 id="browse-topic-title">{t("blogBrowseTopicTitle")}</h2>
        <div className="blog-hub-topic-list" role="list">
          {topicList.map((topic) => (
            <button
              key={topic}
              type="button"
              role="listitem"
              className={selectedCategory === topic ? "is-active" : ""}
              onClick={() => {
                if (BLOG_CATEGORIES.some((item) => item.key === topic)) {
                  setSelectedCategory(topic);
                  return;
                }
                setSearchKeyword(topic);
              }}
            >
              {getCategoryLabel(topic, topic)}
            </button>
          ))}
        </div>
      </section>

      <section className="subpage-panel blog-hub-cta">
        <h2 className="serif-title">{t("blogCtaTitle")}</h2>
        <p>{t("blogCtaDescription")}</p>
        <div className="blog-hub-cta-actions">
          <Link className="button" to="/contact">
            {t("blogCtaPrimary")}
          </Link>
          <Link className="button ghost" to="/solutions">
            {t("blogCtaSecondary")}
          </Link>
        </div>
      </section>
    </StorefrontLayout>
  );
}
